import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BrandLogo } from "./Logo";
import { CALENDLY_URL } from "../data/content";

// Full scope in primary nav (owner direction 31 Oct 2026): all four services
// as direct links at wide widths, an equal "Services" menu at intermediate
// widths, all four listed explicitly in the mobile menu. How We Work and Fees
// stay reachable via mobile menu + footer — nothing orphaned.
const SERVICES = [
  { to: "/buyer-advocacy", label: "Buyer Advocacy" },
  { to: "/suburbiq", label: "SuburbIQ" },
  { to: "/property-portfolio-assessment", label: "Property & Portfolio Assessment" },
  { to: "/vendor-advocacy", label: "Vendor Advocacy" },
];

// Context-aware header action: compact "Book a call" label globally (the full
// service-specific CTA lives in each page's hero); the aria-label carries the
// context. SuburbIQ keeps its immediate purchase action. Logo never shrinks.
const CTA_BY_PATH = {
  "/buyer-advocacy": { label: "Book a call", aria: "Book a Buyer Advocacy call", href: CALENDLY_URL, external: true },
  "/property-portfolio-assessment": { label: "Book a call", aria: "Book an assessment call", href: CALENDLY_URL, external: true },
  "/vendor-advocacy": { label: "Book a call", aria: "Book a Vendor Advocacy call", href: CALENDLY_URL, external: true },
  "/suburbiq": { label: "Buy SuburbIQ", aria: "Buy SuburbIQ — $220 incl. GST", href: "/suburbiq#order", external: false },
};
const DEFAULT_CTA = { label: "Book a call", aria: "Book a 15-minute introductory call", href: CALENDLY_URL, external: true };

const HeaderCta = ({ cta, className, testid, onClick }) =>
  cta.external ? (
    <a href={cta.href} target="_blank" rel="noopener noreferrer" aria-label={cta.aria} data-testid={testid} onClick={onClick} className={className}>
      {cta.label}
    </a>
  ) : (
    <Link to={cta.href} aria-label={cta.aria} data-testid={testid} onClick={onClick} className={className}>
      {cta.label}
    </Link>
  );

const linkCls = (active) =>
  `text-base font-medium whitespace-nowrap transition-colors duration-300 ${
    active ? "text-goldlight" : "text-ivory/75 hover:text-ivory"
  }`;

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const location = useLocation();
  const dropRef = useRef(null);
  const serviceActive = SERVICES.some((s) => location.pathname === s.to);
  const cta = CTA_BY_PATH[location.pathname] || DEFAULT_CTA;

  useEffect(() => {
    setOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setDropOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-charcoal border-b border-white/10" data-testid="site-nav">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-16 h-[88px] lg:h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="80 Metrics home"
          onClick={() => setOpen(false)}
          className="shrink-0 min-w-[3.5rem] lg:min-w-[4rem]"
        >
          <BrandLogo className="h-14 lg:h-16 w-auto max-w-none object-contain" />
        </Link>

        {/* Wide desktop (xl+): all four services as direct links */}
        <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
          {SERVICES.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => linkCls(isActive)}
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/about" data-testid="nav-about" className={({ isActive }) => linkCls(isActive)}>
            About
          </NavLink>
          <NavLink to="/insights" data-testid="nav-resources" className={({ isActive }) => linkCls(isActive)}>
            Resources
          </NavLink>
          <HeaderCta
            cta={cta}
            testid="nav-cta"
            className="ml-2 px-5 py-3 bg-gold text-ink text-base font-semibold whitespace-nowrap transition-[background-color] duration-300 hover:bg-ivory"
          />
        </nav>

        {/* Intermediate (lg–xl): equal Services menu */}
        <nav className="hidden lg:flex xl:hidden items-center gap-8" aria-label="Primary">
          <div className="relative" ref={dropRef}>
            <button
              data-testid="nav-services"
              aria-haspopup="true"
              aria-expanded={dropOpen}
              onClick={() => setDropOpen((v) => !v)}
              className={`${linkCls(serviceActive)} inline-flex items-center gap-1.5`}
            >
              Services
              <span aria-hidden="true" className={`inline-block text-xs transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`}>▾</span>
            </button>
            <AnimatePresence>
              {dropOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-0 top-full mt-3 w-80 border border-white/10 bg-charcoal shadow-2xl"
                  data-testid="nav-services-menu"
                >
                  {SERVICES.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      data-testid={`nav-service-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`block border-b border-white/5 px-6 py-4 text-base font-medium last:border-b-0 transition-colors duration-200 hover:bg-white/[0.04] ${
                        location.pathname === s.to ? "text-goldlight" : "text-ivory"
                      }`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <NavLink to="/about" data-testid="nav-about-mid" className={({ isActive }) => linkCls(isActive)}>
            About
          </NavLink>
          <NavLink to="/insights" data-testid="nav-resources-mid" className={({ isActive }) => linkCls(isActive)}>
            Resources
          </NavLink>
          <HeaderCta
            cta={cta}
            testid="nav-cta-mid"
            className="px-5 py-3 bg-gold text-ink text-base font-semibold whitespace-nowrap transition-[background-color] duration-300 hover:bg-ivory"
          />
        </nav>

        <button
          className="lg:hidden text-ivory p-2"
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <div className="w-6 space-y-1.5">
            <span className={`block h-px bg-current transition-transform duration-300 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
            <span className={`block h-px bg-current transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
            <span className={`block h-px bg-current transition-transform duration-300 ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
          </div>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-charcoal border-t border-white/10"
            data-testid="nav-mobile-menu"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              <p className="pb-2 text-xs font-semibold tracking-[0.2em] uppercase text-ivory/40">Services</p>
              {SERVICES.map((s) => (
                <Link
                  key={s.to}
                  to={s.to}
                  onClick={() => setOpen(false)}
                  className={`py-2.5 text-lg font-medium ${location.pathname === s.to ? "text-goldlight" : "text-ivory/85"}`}
                >
                  {s.label}
                </Link>
              ))}
              <div className="mt-3 border-t border-white/10 pt-4 flex flex-col gap-1">
                <Link to="/about" onClick={() => setOpen(false)} className="py-2.5 text-lg font-medium text-ivory/85">About</Link>
                <Link to="/insights" onClick={() => setOpen(false)} className="py-2.5 text-lg font-medium text-ivory/85">Resources</Link>
                <Link to="/how-we-work" onClick={() => setOpen(false)} className="py-2.5 text-lg font-medium text-ivory/85">How We Work</Link>
                <Link to="/fees" onClick={() => setOpen(false)} className="py-2.5 text-lg font-medium text-ivory/85">Fees</Link>
                <Link to="/contact" onClick={() => setOpen(false)} className="py-2.5 text-lg font-medium text-ivory/85">Contact</Link>
              </div>
              <HeaderCta
                cta={cta}
                testid="nav-mobile-cta"
                onClick={() => setOpen(false)}
                className="mt-4 px-6 py-4 bg-gold text-ink text-center text-base font-semibold"
              />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
